import React, { useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import { getInvitedPeople } from 'src/app/PeopleInvitedToOrg/redux/peopleInvitedToOrg.actions'
import {
  selectPeopleInvitedToOrg,
  selectPeopleInvitedToOrgLoadingFromState,
} from 'src/app/PeopleInvitedToOrg/redux/peopleInvitedToOrg.selectors'
import NoPeople from 'src/components/NoPeople/NoPeople'
import PeopleBoxAndLinks, {
  PeopleFilters,
  PeopleFiltersBody,
} from 'src/components/PeopleBoxAndLinks/PeopleBoxAndLinks'
import { OrganizationRoles } from 'src/enums'
import {
  useBackgroundDispatch,
  useQueryParamArrayHandler,
  useSortable,
} from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import { selectInvitedToOrgListModelsFromState } from 'src/redux/selectors/data/invitedToOrg.selectors'
import { selectUserExplicitOrganizationListModelFromState } from 'src/redux/selectors/data/organizations.selectors'
import { selectQueryParams } from 'src/redux/selectors/router.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'

export interface PeopleInfiniteScrollProps {
  fetch: (filtersBody: PeopleFiltersBody, page?: number) => void
  page?: number
  loading: boolean
  complete: boolean
}

const PeopleInvitedToOrg: React.FC = () => {
  const translate = useTranslate()

  const dispatch = useBackgroundDispatch()
  const invitedToOrgLoading = useSelector(
    selectPeopleInvitedToOrgLoadingFromState,
  )
  const peopleFullPaginatedData = useSelector(selectPeopleInvitedToOrg)
  const invitedListModels = useSelector(selectInvitedToOrgListModelsFromState)
  const organization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )
  const appName = useSelector(selectAppName)
  const queryParams = useSelector(selectQueryParams)

  const [stateQueryParams, setStateQueryParams] = useState(queryParams)
  const [initialLoad, setInitialLoad] = useState(false)

  const fetchInvitedPeople = (
    filtersBody?: PeopleFiltersBody,
    page?: number,
  ): void => {
    if (organization) {
      dispatch(
        getInvitedPeople(organization.id, {
          ...filtersBody,
          page: page || 0,
          count: 10,
        }),
      )
    }
  }

  const peopleInvitedToOrgInfiniteScrollProps: PeopleInfiniteScrollProps = {
    ...peopleFullPaginatedData,
    fetch: fetchInvitedPeople,
  }

  const initialFilterValues: PeopleFilters = useMemo(
    () => ({
      keyword: stateQueryParams.search || '',
      organizationRoles: useQueryParamArrayHandler(
        stateQueryParams.organizationRoles,
      ) as OrganizationRoles[],
      sortDescending: true,
    }),
    [],
  )

  const [keywordFilter, setKeywordFilter] = useState(
    initialFilterValues.keyword,
  )

  const { setSort, sortBy, sortDescending } = useSortable(
    initialFilterValues.sortBy,
    initialFilterValues.sortDescending,
  )

  const initialfiltersBody: PeopleFiltersBody = {
    search: keywordFilter ? `${keywordFilter}` : undefined,
    sortField: sortBy,
    sortDirection: sortDescending === false ? 'ASC' : 'DESC',
  }

  // initial load
  useEffect((): void => {
    if (Object.keys(invitedListModels).length === 0 && organization) {
      dispatch(
        getInvitedPeople(organization.id, {
          ...initialfiltersBody,
          page: 0,
          perPage: 10,
        }),
      )
      setInitialLoad(true)
    }
  }, [])

  return (
    <>
      <Helmet>
        <title>{`Invited to Org | ${appName}`}</title>
        <meta
          name="description"
          content={`Invited to Org page of ${appName}`}
        />
      </Helmet>
      <PeopleBoxAndLinks
        peopleInfiniteScrollProps={peopleInvitedToOrgInfiniteScrollProps}
        loading={invitedToOrgLoading}
        noPeopleComponent={
          <NoPeople
            titleText={translate('There are no pending invites')}
            topSubtitleText={translate(
              'People who have been invited to your organization will appear here until they accept or reject the invitation.',
            )}
            buttonText={translate('Invite People')}
          />
        }
        stateQueryParams={stateQueryParams}
        setStateQueryParams={setStateQueryParams}
        keywordFilter={keywordFilter}
        setKeywordFilter={setKeywordFilter}
        setSort={setSort}
        sortBy={sortBy}
        sortDescending={sortDescending}
        initialLoad={initialLoad}
        setInitialLoad={setInitialLoad}
      />
    </>
  )
}

export default PeopleInvitedToOrg
